import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ArrowLeft, FloppyDisk, CheckCircle, MagnifyingGlass, Eye, PencilSimple } from "@phosphor-icons/react";
import { TrackBadge } from "./Sdp";

const mdToHtml = (md) => (md || "")
  .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  .replace(/^### (.*)$/gm, "<h3>$1</h3>").replace(/^## (.*)$/gm, "<h2>$1</h2>").replace(/^# (.*)$/gm, "<h1>$1</h1>")
  .replace(/\*\*(.+?)\*\*/g, "<b>$1</b>").replace(/\*(.+?)\*/g, "<i>$1</i>")
  .replace(/^\- (.*)$/gm, "<li>$1</li>").replace(/\n/g, "<br/>");

export default function SdpDraft() {
  const { did } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [text, setText] = useState("");
  const [preview, setPreview] = useState(false);
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("all");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try { const { data } = await api.get(`/sdp/drafts/${did}`); setData(data); setText(data.draft.draft_text || ""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed to load draft."); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [did]);

  const save = async () => {
    setSaving(true);
    try { await api.put(`/sdp/drafts/${did}`, { draft_text: text }); toast.success("Saved."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setSaving(false);
  };
  const complete = async () => {
    try { await api.post(`/sdp/drafts/${did}/complete`); toast.success("Marked complete — sent for review. Child ticket completed."); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const filtered = useMemo(() => {
    const list = data?.industry_comments || [];
    const ql = q.trim().toLowerCase();
    return list.filter((c) => {
      const v = (c.vote_position || "").toLowerCase();
      const matchPos = pos === "all" || (pos === "affirmative" && (v.startsWith("affirm") || v === "yes")) || (pos === "negative" && (v.startsWith("neg") || v === "no"));
      const matchQ = !ql || `${c.entity_name} ${c.segment} ${c.comment_text}`.toLowerCase().includes(ql);
      return matchPos && matchQ;
    });
  }, [data, q, pos]);

  if (!data) return <p className="text-sm text-muted-foreground p-6">Loading…</p>;
  const d = data.draft, p = data.project || {};
  const locked = ["in_review", "approved", "submitted"].includes(d.status);

  return (
    <div data-testid="sdp-draft-workspace" className="h-[calc(100vh-120px)] flex flex-col">
      <button onClick={() => nav(`/sdp/${p.id}`)} className="flex items-center gap-1 text-sm text-muted-foreground mb-2 hover:text-foreground"><ArrowLeft size={14} />Back to project</button>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h1 className="text-xl font-bold flex items-center gap-2">{d.requirement_reference} <TrackBadge track={p.process_track} /></h1>
          <p className="text-xs text-muted-foreground">{p.name} · status: {d.status.replace("_", " ")}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" className="rounded-none" data-testid="sdp-draft-save" onClick={save} disabled={saving || locked}><FloppyDisk size={15} className="mr-1" />{saving ? "Saving…" : "Save"}</Button>
          <Button size="sm" className="rounded-none" data-testid="sdp-draft-complete" onClick={complete} disabled={locked}><CheckCircle size={15} className="mr-1" />Complete & send for review</Button>
        </div>
      </div>
      {d.instructions && <div className="text-xs bg-secondary/50 border border-border p-2 mb-3"><b>Brief:</b> {d.instructions}</div>}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 flex-1 min-h-0">
        {/* Left: drafting */}
        <div className="flex flex-col min-h-0 border border-border">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border bg-secondary/40">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Your technical comment (Markdown)</span>
            <button onClick={() => setPreview(!preview)} className="text-xs flex items-center gap-1 text-[#071B3E] font-semibold" data-testid="sdp-draft-preview-toggle">{preview ? <><PencilSimple size={13} />Edit</> : <><Eye size={13} />Preview</>}</button>
          </div>
          {preview ?
            <div data-testid="sdp-draft-preview" className="flex-1 overflow-auto p-4 prose prose-sm max-w-none text-sm" dangerouslySetInnerHTML={{ __html: mdToHtml(text) }} /> :
            <Textarea data-testid="sdp-draft-editor" value={text} onChange={(e) => setText(e.target.value)} disabled={locked}
              placeholder="# Position\nProvide the entity's technical justification against this requirement…" className="flex-1 rounded-none border-0 font-mono text-sm resize-none focus-visible:ring-0" />}
          {locked && <div className="text-[11px] text-amber-700 bg-amber-50 border-t border-amber-200 px-3 py-1">This draft is {d.status.replace("_", " ")} and locked from editing.</div>}
        </div>

        {/* Right: industry comments feed */}
        <div className="flex flex-col min-h-0 border border-border">
          <div className="px-3 py-2 border-b border-border bg-secondary/40">
            <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">What peer utilities are submitting ({filtered.length})</span>
            <div className="flex gap-2 mt-2">
              <div className="relative flex-1"><MagnifyingGlass size={13} className="absolute left-2 top-2.5 text-muted-foreground" /><Input data-testid="sdp-feed-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter entity / text…" className="rounded-none h-8 pl-7 text-xs" /></div>
              <Select value={pos} onValueChange={setPos}>
                <SelectTrigger className="rounded-none h-8 w-32 text-xs" data-testid="sdp-feed-filter"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="all">All positions</SelectItem><SelectItem value="affirmative">Affirmative</SelectItem><SelectItem value="negative">Negative</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex-1 overflow-auto p-2 space-y-2" data-testid="sdp-feed">
            {filtered.length === 0 ? <p className="text-xs text-muted-foreground p-3">No industry comments match. Import them from the project's Industry Comments tab.</p> :
              filtered.map((c) => (
                <div key={c.id} className="border border-border p-2 text-xs bg-card">
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{c.entity_name} <span className="text-muted-foreground font-normal">· Seg {c.segment}</span></span>
                    <span className={`text-[10px] px-1.5 py-0.5 ${/affirm|yes/i.test(c.vote_position) ? "bg-emerald-100 text-emerald-800" : /neg|no/i.test(c.vote_position) ? "bg-red-100 text-red-800" : "bg-slate-100 text-slate-700"}`}>{c.vote_position || "—"}</span>
                  </div>
                  {c.comment_text && <p className="mt-1 text-muted-foreground">{c.comment_text}</p>}
                </div>
              ))}
          </div>
        </div>
      </div>
    </div>
  );
}
